<template>
	<Headroom :offset="20" @pin="onHeaderPin" @unpin="onHeaderUnpin">
		<header class="main-header relative z-50 flex h-20 flex-wrap">
			<div
				class="main-header--nav relative z-20 flex w-full justify-between px-5 duration-200"
			>
				<div class="flex w-60 items-center">
					<CarbarLink href="/">
						<IconCarbarLogo />
					</CarbarLink>
				</div>
				<div v-if="showMenu" class="flex-end ml-auto flex">
					<button
						class="hamburger hamburger--spring js-hamburger relative ml-auto flex items-center lg:hidden"
						:class="[showMobileMenu ? 'is-active' : '']"
						type="button"
						@click="toggleMobileMenu"
					>
						<span class="hamburger-box">
							<span class="hamburger-inner"></span>
						</span>
					</button>
				</div>

				<div
					v-if="showMenu"
					class="hidden flex-1 justify-center lg:flex"
				>
					<ul class="m-0 flex list-none gap-x-2">
						<li class="flex cursor-pointer">
							<CarbarLink
								class="flex items-center px-4 font-medium delay-75 duration-200"
								font-size="text-base"
								to="/dashboard/my-car"
								:underline="false"
								:text-color="isActive('/dashboard/my-car')"
							>
								My car
							</CarbarLink>
						</li>
						<li class="flex cursor-pointer">
							<CarbarLink
								class="flex items-center px-4 font-medium delay-75 duration-200"
								font-size="text-base"
								to="/dashboard/account"
								:underline="false"
								:text-color="isActive('/dashboard/account')"
							>
								Account
							</CarbarLink>
						</li>
					</ul>
				</div>

				<div
					v-if="showMenu"
					class="hidden w-60 flex-1 items-center justify-end lg:flex lg:flex-none"
				>
					<div
						class="flex h-full items-center space-x-4 lg:space-x-3"
					>
						<div v-if="auUser.getIsSigningOut" class="h-4 w-4">
							<IconsIconLoading />
						</div>
						<DropdownUserMenu
							v-if="auUser.isLoggedIn"
							:menu-items="userMenu"
							class="group cursor-pointer"
						>
							<template #menu-button>
								<UserAccountIcon
									:is-novated="auUser.isNovated"
								/>
							</template>
							<template #caret>
								<div class="on-open-rotate-90 ml-1">
									<IconArrowDown
										class="text-gray-500 duration-200 group-hover:rotate-90 group-hover:text-blue-400"
									/>
								</div>
							</template>
						</DropdownUserMenu>
					</div>
				</div>
			</div>

			<div
				class="mobile-menu absolute inset-0 h-screen max-h-screen w-full overflow-y-auto bg-white pb-10 pt-20 duration-200 lg:hidden"
				:class="[
					showMobileMenu
						? 'translate-x-0 opacity-100'
						: '-translate-x-full opacity-0',
				]"
			>
				<div class="mt-1 px-4">
					<div
						class="flex flex-col items-center justify-center gap-y-4 rounded bg-gray-50 p-4"
					>
						<div v-if="auUser.isLoggedIn" class="w-full">
							<div
								class="mb-6 flex w-full items-center justify-between"
							>
								<div class="pl-1">
									<span
										class="text-xl font-semibold text-gray-700"
									>
										Welcome back, {{ auUser.firstName }}.
									</span>
								</div>
								<div v-if="auUser.user" class="pr-1">
									<UserAccountIcon
										:is-novated="auUser.isNovated"
									/>
								</div>
							</div>

							<div class="w-full">
								<ul
									class="m-0 flex list-none flex-col gap-y-5 lg:space-x-5"
								>
									<li
										v-for="(
											userMenuItem, index
										) in userMenu"
										:key="index"
									>
										<!-- Replace Last Item Style - Logout -->
										<div
											v-if="index == userMenu.length - 1"
											class="mt-2 w-full rounded bg-gray-600 p-4 text-center"
										>
											<a
												class="font-medium text-white"
												:href="
													userMenuItem.href
														? userMenuItem.href
														: ''
												"
												@click="
													onClickMenuItem(
														userMenuItem,
														$event
													)
												"
											>
												{{ userMenuItem.label }}
											</a>
										</div>
										<div v-else>
											<a
												class="px-1 font-medium text-gray-600"
												:href="
													userMenuItem.href
														? userMenuItem.href
														: ''
												"
												@click="
													onClickMenuItem(
														userMenuItem,
														$event
													)
												"
											>
												{{ userMenuItem.label }}
											</a>
										</div>
									</li>
								</ul>
							</div>
						</div>
					</div>
				</div>
			</div>
		</header>
	</Headroom>

	<div class="h-20" />
</template>

<script setup lang="ts">
import IconArrowDown from '@/assets/svg/common/ico-arrow_down.svg'
import IconCarbarLogo from '@/assets/svg/logo-carbar-v3.svg'
import DropdownUserMenu from '@/components/dropdowns/DropdownUserMenu.vue'
import UserAccountIcon from '@/components/headers/UserAccountIcon.vue'
import CarbarLink from '@/components/links/CarbarLink.vue'
import { useCarbarStore } from '@/stores/carbar'
import { useCarbarAuAuthStore } from '@/stores/carbar-au-auth'
import Headroom from '@/vendor/headroom/headroom.vue'

const auUser = useCarbarAuAuthStore()
const carbar = useCarbarStore()
const { toggleMobileMenu } = headerModule()
const route = useRoute()

const showMobileMenu = computed(() => carbar.header.showMobileMenu)

const isActive = (path: string) => {
	return route.path.startsWith(path) ? 'text-blue-400' : 'text-gray-575'
}

defineProps({
	showMenu: {
		type: Boolean,
		default: true,
		required: false,
	},
	showCampaign: {
		type: Boolean,
		default: true,
		required: false,
	},
})

const userMenu = ref([
	{
		label: 'My car',
		href: `/dashboard/my-car`,
	},
	{
		label: 'Account',
		href: `/dashboard/account`,
	},
	{
		label: 'Help & support',
		href: `https://support.carbar.com.au`,
	},
	{
		label: 'Logout',
		onclick: () => {
			signOut()
		},
	},
])

const onHeaderPin = () => {
	document.body.classList.add(...['header-pinned'])
}

const onHeaderUnpin = () => {
	document.body.classList.remove(...['header-pinned'])
}

const onClickMenuItem = (menuItem, e) => {
	if (!menuItem.href) {
		e.preventDefault()
	}

	if (menuItem.onclick) {
		menuItem.onclick()
	}
}
</script>
